import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UserInfo } from '@thehubz/client/data-access-api';
import { AnyType, EditorConfig } from '@thehubz/shared/model';
import { ContentChange, QuillModules, QuillToolbarConfig } from 'ngx-quill';
import Quill from 'quill';
import 'quill-mention';
import { AllPeople, ApiCallsService } from './api-calls/api-calls.service';
import { Editor2UtilService } from './api-calls/editor2-util.service';
import { TaggingOptions } from './model/tagging-option.model';

@Component({
  selector: 'sp-editor',
  templateUrl: './editor2.component.html',
  styleUrls: ['./editor2.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Editor2Component),
      multi: true,
    },
  ],
})
export class Editor2Component implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder = 'Insert text here...';
  @Input() maxLength?: number;
  @Input() formControlName?: string;
  @Input() taggingOptions?: TaggingOptions;
  @Input() toolbarConfig?: QuillToolbarConfig;

  @Output() usersList = new EventEmitter<UserInfo[]>(); // emit tagged user list
  @Output() descriptionText = new EventEmitter<string>(); // emit editor text

  isDisabled = false;
  initValue?: string;

  charCount = 0;

  private mentionsList: string[] = [];
  private usersSet: UserInfo[] = [];

  private onChange!: (value: string | null) => void;
  private onTouched!: () => void;

  editorModules: QuillModules = {
    toolbar: EditorConfig.toolbar,
    mention: {
      mentionDenotationChars: ['@'],
      defaultMenuOrientation: 'bottom',
      positioningStrategy: 'fixed',
      isolateCharacter: true, // isolated denotation character (avoid mentioning in an email)
      dataAttributes: ['email', 'name'],
      source: async (searchTerm: string, renderList: AnyType) => {
        if (this.taggingOptions) {
          const matchedPeople = await this.apiCallsService.mentionsUsers(
            searchTerm,
            this.taggingOptions,
            this.usersSet.map((user) => user.id),
          );
          renderList(matchedPeople);
        }
      },
      renderItem: (item: AllPeople) => {
        switch (item.name) {
          case 'task': {
            return `<span class="font-bold flex items-center"><mat-icon class="material-icons-outlined mr-2">assignment_turned_in</mat-icon>${item.value}</span>`;
          }
          case 'project': {
            return `<span class="font-bold flex items-center"><mat-icon class="material-icons-outlined mr-2">assignment_ind</mat-icon>${item.value}</span>`;
          }
          case 'team': {
            return `<span class="font-bold flex items-center"><mat-icon class="material-icons-outlined mr-2">groups</mat-icon>${item.value}</span>`;
          }
          default: {
            return `${item.highlightName}`;
          }
        }
      },
      onOpen: () => {
        setTimeout(() => {
          window.addEventListener('click', this.closeMentionHelper);
        }, 100);
      },
      onClose: () => {
        window.removeEventListener('click', this.closeMentionHelper);
      },
      onSelect: (item: DOMStringMap, insertItem: AnyType) => {
        this.mentionsList.push(JSON.stringify({ id: item['id'] || '', name: item['name'] || '', email: item['email'] || '' }));
        this.usersSet = this.editor2UtilService.removeListDuplicate(this.mentionsList);
        this.usersList.emit(this.usersSet);
        insertItem(item);
      },
    },
  };

  constructor(
    private apiCallsService: ApiCallsService,
    private editor2UtilService: Editor2UtilService,
  ) {}

  ngOnInit(): void {
    if (!this.taggingOptions) {
      delete this.editorModules['mention'];
    }

    if (this.toolbarConfig) {
      this.editorModules.toolbar = this.toolbarConfig;
    }
  }

  ngOnDestroy(): void {
    this.apiCallsService.clear();
  }

  /**
   * onContentChanged event method
   * @param event
   */
  editorInput(event: ContentChange) {
    // get the deleted mention
    const deletedMention = this.editor2UtilService.findRemovedMention(event);
    if (deletedMention) {
      const index = this.usersSet.findIndex((set) => set.email === deletedMention.insert.mention.email || set.id === deletedMention.insert.mention.id);
      if (index > -1) {
        this.usersSet.splice(index, 1); // remove mention from usersSet
        this.mentionsList.splice(index, 1); // remove mention from mentionsList
        this.usersList.emit(this.usersSet); // emit the usersSet to parent component
      }
    }

    const content = event.html;
    if (content === null || content.trim() === '') {
      this.onChange('');
      this.onTouched();
      this.charCount = 0;
      this.descriptionText.emit('');
      return;
    }

    const contentLength = event.editor.getLength();
    if (this.maxLength && contentLength > this.maxLength) {
      event.editor.deleteText(this.maxLength - 1, event.editor.getLength());
    } else {
      this.onChange(content);
    }
    this.charCount = event.editor.getLength() - 1;
    this.descriptionText.emit(this.editor2UtilService.truncateWord(event.text, 125));
  }

  editorBlur() {
    this.onTouched();
  }

  onInputChange() {
    this.onTouched();
  }

  editorCreated(editor: Quill) {
    this.charCount = editor.getLength() - 1;
  }

  writeValue(value: string): void {
    this.initValue = value;
  }

  registerOnChange(fn: AnyType): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: AnyType): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  private closeMentionHelper() {
    setTimeout(() => {
      document.getElementsByClassName('ql-mention-list-container')[0]?.remove();
    }, 100);
  }
}
